import { Dispatch } from "redux";
import { NavigateFunction } from "react-router-dom";
import WebService from "../../../Services/WebService";
import {
  profileDetail,
  registrationId,
  storeAccessData,
  storeActiveProfiles,
  storeActiveSubscription,
  storeLoginAccountTypes,
  storePrefferedCurrency,
  storeProfileCompletionFlags,
} from "../../../config/action";
import {
  ACCOUNT_TYPE,
  ACCOUNT_TYPE_API,
  LOGIN_TYPE,
  META_CONST,
  NAVIGATION_CONST,
} from "../../../assets/constants/constants";
import { RegistrationIds } from "../../../assets/interface/primeSharkWebsite_interface";
import { requestForToken } from "../../../requestPermission";
import { useSelector } from "react-redux";
import { trackPixelEvent } from "../../pixelUtils";
import { getProfileJourneyInfo } from "../../../Services/CommonFunction";

interface MeCallParams {
  navigate: NavigateFunction;
  dispatch: Dispatch;
  login_type: string;
  account_type: string;
  coming_from: string;
  FCMDeviceToken?: string;
}

export const MeCallRevised = async ({
  navigate,
  dispatch,
  login_type,
  account_type,
  coming_from,
  FCMDeviceToken,
}: MeCallParams) => {
  const storeImpIdsInRedux = (impIds: RegistrationIds) => {
    dispatch(registrationId(impIds));
  };

  return WebService.getAPI({
    action: login_type === "mentor" ? "mentor/me" : "me",
    body: null,
    isShowError: false,
  })
    .then((res: any) => {
      if (res && login_type === LOGIN_TYPE.MENTOR.toLocaleLowerCase()) {
        // Always hydrate the store with the CURRENT mentor so the header (and
        // anything else reading profileDetail) never shows stale/empty data,
        // regardless of navigation.
        if (res?.mentor_user?.currency) {
          dispatch(storePrefferedCurrency(res.mentor_user.currency));
        }
        dispatch(profileDetail(res?.mentor_user || {}));

        const currentPath = window.location.pathname;
        const isOnMentorApp =
          currentPath.startsWith("/mentor/") &&
          currentPath !== "/mentor/login" &&
          currentPath !== "/mentor/register";
        if (!isOnMentorApp) {
          navigate(`/mentor/session-requests`, { replace: true });
          requestForToken(FCMDeviceToken, dispatch, true);
        }
        return;
      }

      const userInfo = res?.info || {};
      localStorage.setItem("user_id", userInfo?.id);
      // Also store in a cookie shared across the domain (cleared on logout).
      WebService.setUserIdCookie(userInfo?.id);

      let {
        has_active_subscription,
        has_subscription_expired,
        has_investment_profile_completed,
        has_investment_subscription,
        has_want_to_sell_subscription,
        has_want_to_sell_profile_completed,
        has_investor_subscription,
        has_want_to_grow_subscription,
        has_want_to_grow_profile_completed,
        has_investor_profile_completed,
        has_investment_free_subscription,
        has_want_to_sell_free_subscription,
        has_want_to_grow_free_subscription,
        narration,
      } = userInfo;

      let {
        is_investor_profile_verified,
        is_investment_profile_verified,
        is_want_to_grow_profile_verified,
        is_want_to_sell_profile_verified,
        ai_scores,
      } = userInfo?.basic_info;


      if (login_type === LOGIN_TYPE.INVESTOR.toLocaleLowerCase()) {
        localStorage.setItem(
          "is_investor_profile_verified",
          is_investor_profile_verified,
        );
      } else if (login_type === LOGIN_TYPE.ENTREPRENEUR.toLocaleLowerCase()) {
        localStorage.setItem(
          "is_investment_profile_verified",
          is_investment_profile_verified,
        );
        localStorage.setItem(
          "is_want_to_grow_profile_verified",
          is_want_to_grow_profile_verified,
        );
        localStorage.setItem(
          "is_want_to_sell_profile_verified",
          is_want_to_sell_profile_verified,
        );
      }

      const userSubscription = userInfo?.user_subscription;
      if (userSubscription) {
        dispatch(storeActiveSubscription(userSubscription));
      }
      localStorage.setItem("x-info-id", userInfo?.basic_info?.uuid);
      dispatch(
        storeProfileCompletionFlags({
          has_active_subscription: userInfo?.has_active_subscription,
          has_subscription_expired: userInfo?.has_subscription_expired,
          has_investment_profile_completed:
            userInfo?.has_investment_profile_completed,
          has_investment_subscription: userInfo?.has_investment_subscription,
          has_want_to_sell_subscription:
            userInfo?.has_want_to_sell_subscription,
          has_want_to_sell_profile_completed:
            userInfo?.has_want_to_sell_profile_completed,
          has_want_to_grow_subscription:
            userInfo?.has_want_to_grow_subscription,
          has_want_to_grow_profile_completed:
            userInfo?.has_want_to_grow_profile_completed,
          has_investor_subscription: userInfo?.has_investor_subscription,
          has_investor_profile_completed:
            userInfo?.has_investor_profile_completed,
          has_free_subscription: userInfo?.has_free_subscription,
          has_investment_free_subscription:
            userInfo?.has_investment_free_subscription,
          has_investor_free_subscription:
            userInfo?.has_investor_free_subscription,
          has_want_to_grow_free_subscription:
            userInfo?.has_want_to_grow_free_subscription,
          has_want_to_sell_free_subscription:
            userInfo?.has_want_to_sell_free_subscription,
          has_investment_subs: userInfo?.has_investment_subs,
          has_investor_subs: userInfo?.has_investor_subs,
          has_want_to_grow_subs: userInfo?.has_want_to_grow_subs,
          has_want_to_sell_subs: userInfo?.has_want_to_sell_subs,
          has_investor_tab_access: userInfo?.has_investor_tab_access,
        }),
      );

      let basic_info = userInfo?.basic_info;
      let loginType = login_type.toLocaleLowerCase();
      let accountType = account_type.toLocaleLowerCase();
      let business_info =
        loginType === "entrepreneur"
          ? basic_info?.entrepreneur_business_details
          : basic_info?.investor_business_details;
      let impIds = {
        uuid: userInfo?.uuid,
        business_uuid: business_info?.uuid,
        account_type: accountType?.toUpperCase(),
        login_type: loginType?.toLocaleUpperCase(),
      };

      let business_uuid =
        business_info?.uuid;
      dispatch(registrationId(impIds));

      let user_type_info = basic_info?.user_type_info;

      let profileInfo = basic_info?.profile_info;
      if (profileInfo && loginType === "entrepreneur") {
        const typeMap: Record<string, string> = {
          has_investment: "investment",
          has_want_to_grow: "want-to-grow",
          has_want_to_sell: "want-to-sell",
        };

        // current frontend matching key
        const currentKey = Object.keys(typeMap).find(
          (key) => typeMap[key] === accountType,
        );
        if (currentKey && profileInfo[currentKey]) {
          loginType = "entrepreneur";
        } else {
          const activeType = Object.keys(typeMap).find(
            (key) => profileInfo[key],
          );

          if (activeType) {
            loginType = "entrepreneur";
            accountType = typeMap[activeType];
          }
        }
      }

      dispatch(
        storeLoginAccountTypes({
          loginType: loginType,
          account_type: accountType,
        }),
      );
      if (res?.info?.currency)
        dispatch(storePrefferedCurrency(res?.info?.currency));
      dispatch(profileDetail(userInfo));
      if (userInfo?.status === "GUEST_PENDING") {
        const impIds = {
          uuid: userInfo?.uuid,
          business_uuid: userInfo?.basic_info?.uuid,
        };
        dispatch(registrationId(impIds));
        if (loginType?.toUpperCase() === LOGIN_TYPE.ENTREPRENEUR) {
          navigate(`/${loginType}/${accountType}/register`, { replace: true });
        }
        if (loginType?.toUpperCase() === LOGIN_TYPE.INVESTOR) {
          navigate(`/${loginType}/register`, { replace: true });
        }
      } else if (
        login_type?.toLocaleLowerCase() ===
        LOGIN_TYPE.ENTREPRENEUR.toLocaleLowerCase()
      ) {
        if (coming_from === NAVIGATION_CONST.REGISTRATION) {
          let data = {
            name: `${userInfo?.first_name} ${userInfo?.last_name}`,
            country_code: userInfo?.country_code,
            mobile_number: `${userInfo?.dial_code}-${userInfo?.mobile_number}`,
            email: userInfo?.email,
          };
          trackPixelEvent(META_CONST.REGISTRATION_COMPLETE, {
            params: data,
            status: "success",
          });
          if (typeof window !== "undefined") {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "registration",
              name: `${userInfo?.first_name} ${userInfo?.last_name}`,
              country_code: userInfo?.country_code,
              mobile_number: `${userInfo?.dial_code}-${userInfo?.mobile_number}`,
              email: userInfo?.email,
            });
          }

          localStorage.setItem("Tab_Selected", "home");
          // if (
          //   account_type?.toLowerCase() === ACCOUNT_TYPE["WANT-TO-GROW"] ||
          //   account_type?.toLowerCase() === ACCOUNT_TYPE.INVESTMENT
          // )
          getProfileJourneyInfo(accountType, business_uuid, dispatch);
          navigate(`/${loginType}/${accountType}/profile-review`, {
            replace: true,
          });
        } else if (coming_from === NAVIGATION_CONST.SUBSCRIPTION) {
          const impIds: RegistrationIds = {
            uuid: userInfo?.uuid,
            business_uuid: business_uuid,
            account_type: accountType,
            login_type: loginType,
          };
          storeImpIdsInRedux(impIds);
          navigate(`/${loginType}/${accountType}/my-subscription`);
        } else if (coming_from === NAVIGATION_CONST.LOGIN) {
          if (
            account_type === "investment" &&
            is_investment_profile_verified === "REJECTED"
          ) {
            navigate("/profile-rejected", { replace: true });
          } else if (!has_active_subscription) {
            navigate(`/${login_type}/${account_type}/subscriptions`, {
              replace: true,
            });
          } else {
            // if (
            //   accountType === ACCOUNT_TYPE_API["WANT_TO_GROW"] ||
            //   accountType === ACCOUNT_TYPE_API.INVESTMENT
            // )
            getProfileJourneyInfo(accountType, business_uuid, dispatch);
            localStorage.setItem("Tab_Selected", "home");
            navigate(`/${loginType}/${accountType}/home`, {
              replace: true,
            });
          }
        } else {
          const currentPath = window.location.pathname;
          const shouldRedirect = currentPath === "/" || currentPath === "/login" || currentPath === "/homepage" || currentPath.endsWith("/login") || currentPath.endsWith("/register");
          if (shouldRedirect) {
            navigate(`/${loginType}/${accountType}/home`, { replace: true });
          }
        }

        let data = {
          name: `${userInfo?.first_name} ${userInfo?.last_name}`,
          country_code: userInfo?.country_code,
          mobile_number: `${userInfo?.dial_code}-${userInfo?.mobile_number}`,
          email: userInfo?.email,
        };
        trackPixelEvent(META_CONST.LOGIN, {
          params: data,
          status: "success",
        });
      } else if (login_type === LOGIN_TYPE.INVESTOR.toLocaleLowerCase()) {
        if (coming_from === NAVIGATION_CONST.REGISTRATION) {
          localStorage.setItem("Tab_Selected", "capital_bridge");
          getProfileJourneyInfo(loginType, business_uuid, dispatch);
          navigate(`/${loginType}/profile-review`, {
            replace: true,
          });
        } else if (coming_from === NAVIGATION_CONST.LOGIN) {
          if (is_investor_profile_verified === "PENDING") {
            getProfileJourneyInfo(loginType, business_uuid, dispatch);
            localStorage.setItem("Tab_Selected", "home");
            navigate(`/${loginType}/home`, { replace: true });
            return;
          } else if (is_investor_profile_verified === "REJECTED") {
            navigate("/profile-rejected", { replace: true });
            return;
          } else if (is_investor_profile_verified === "VERIFIED") {
            getProfileJourneyInfo(loginType, business_uuid, dispatch);
            localStorage.setItem("Tab_Selected", "home");
            navigate(`/${loginType}/home`, { replace: true });
          }
        } else if (coming_from === NAVIGATION_CONST.MY_PROFILE) {
          if (
            is_investor_profile_verified === "PENDING" ||
            is_investor_profile_verified === "VERIFIED"
          ) {
            localStorage.setItem("Tab_Selected", "home");
            navigate(`/${loginType}/home`);
          }
        } else {
          const currentPath = window.location.pathname;
          const shouldRedirect = currentPath === "/" || currentPath === "/login" || currentPath === "/homepage" || currentPath.endsWith("/login") || currentPath.endsWith("/register");
          if (shouldRedirect) {
            navigate(`/${loginType}/home`, { replace: true });
          }
        }
      }

      requestForToken(FCMDeviceToken, dispatch);
    })
    .catch((error: any) => {
      return error;
    })
    .finally(() => {
      WebService.removeLoader("login-btn");
    });
};
